// Часть разбиения MapView.vue (фаза 2) - маркер «вы здесь» с конусом
// направления, отслеживание позиции (GPS + IP-фолбэк) и heading устройства.
//
// mapRotationMode принимается готовым Ref от useMapRotation3D.ts (создаётся
// раньше, см. MapView.vue) - GPS только читает его, обратной зависимости нет.
// maybeCenterInitially принимается от useNodeMarkers.ts по той же причине.
import * as maplibregl from "maplibre-gl";
import type { Marker } from "maplibre-gl";
import type { Ref } from "vue";
import { ref } from "vue";
import type { FeatureCollection } from "geojson";
import { ACCURACY_SOURCE, EMPTY_FEATURES } from "../map/constants";
import type { MapCtx } from "../map/mapContext";
import { markerElement } from "../map/mapUiHelpers";
import { destinationPoint } from "../utils/routeLineStyle";
import type { MapRotationMode } from "./useMapRotation3D";

// Замена zIndexOffset из Leaflet - GPS-точка должна лежать выше всех
// остальных маркеров (POI/узлы/курсор маршрута).
const USER_MARKER_Z = 2000;

// circle paint у MapLibre - фиксированный радиус в пикселях, а не в метрах
// на местности, как нужно для радиуса точности GPS (аналог L.circle из
// Leaflet). Поэтому нарисован вручную полигоном через тот же геодезический
// destinationPoint, что и стрелки маршрута.
const ACCURACY_CIRCLE_POINTS = 32;

export interface UseGpsTrackingOptions {
  mapRotationMode: Ref<MapRotationMode>;
  maybeCenterInitially: (lng: number, lat: number) => void;
  markProgrammaticRotate: () => void;
  onGpsPosition?: (lat: number, lng: number) => void;
}

export function useGpsTracking(ctx: MapCtx, options: UseGpsTrackingOptions) {
  let userMarker: Marker | null = null;
  let userAccuracy: { lng: number; lat: number; radiusM: number } | null = null;
  let geoWatchId: number | null = null;
  let orientationAttached = false;
  let ipFallbackAttempted = false;
  let lastHeadingDeg: number | null = null;

  const locating = ref(false);
  const hasLocation = ref(false);
  const locationSource = ref<"gps" | "ip" | null>(null);

  const createUserLocationElement = () => {
    const el = markerElement(
      "user-location-marker-icon",
      `
      <div class="user-location-marker">
        <div class="user-location-marker__heading" data-heading-cone></div>
        <div class="user-location-marker__pulse"></div>
        <div class="user-location-marker__dot"></div>
      </div>
    `
    );
    el.style.zIndex = String(USER_MARKER_Z);
    return el;
  };

  // Конус указывает направление устройства НА ЭКРАНЕ, поэтому рисуется
  // относительно текущего bearing карты (heading минус bearing). Одна формула
  // покрывает все три режима поворота: в north-up (bearing 0) это чистый
  // heading; в manual она компенсирует поворот карты пользователем; в compass
  // обе величины гасят друг друга до ~0 - ровно "фиксированная стрелка вперёд",
  // которая нужна этому режиму.
  const refreshUserHeadingVisual = () => {
    const el = userMarker?.getElement();
    const cone = el?.querySelector<HTMLElement>("[data-heading-cone]");
    if (!el || !cone) return;
    // В режиме compass поворачивается сама карта, поэтому маркер должен
    // выглядеть сплошной стрелкой вперёд, а не качающимся конусом.
    el.classList.toggle("user-location-marker-icon--compass", options.mapRotationMode.value === "compass");
    if (lastHeadingDeg == null || Number.isNaN(lastHeadingDeg)) {
      cone.style.display = "none";
      return;
    }
    const bearing = ctx.getMap()?.getBearing() ?? 0;
    const screenDeg = ((lastHeadingDeg - bearing) % 360 + 360) % 360;
    cone.style.display = "";
    cone.style.transform = `translateX(-50%) rotate(${screenDeg}deg)`;
  };

  const setUserHeading = (heading: number | null) => {
    lastHeadingDeg = heading;
    if (heading != null && !Number.isNaN(heading) && options.mapRotationMode.value === "compass") {
      const map = ctx.getMap();
      options.markProgrammaticRotate();
      // Короткая анимация: события компаса приходят по несколько раз в
      // секунду, длинная анимация ещё бы шла к приходу следующего события, и
      // карта заметно отставала бы от устройства.
      map?.easeTo({ bearing: heading, duration: 120 });
    }
    refreshUserHeadingVisual();
  };

  const accuracyCircleFeature = (): FeatureCollection => {
    if (!userAccuracy) return EMPTY_FEATURES;
    const ring: [number, number][] = [];
    for (let i = 0; i <= ACCURACY_CIRCLE_POINTS; i++) {
      const [lat, lng] = destinationPoint(
        userAccuracy.lat,
        userAccuracy.lng,
        (i / ACCURACY_CIRCLE_POINTS) * 360,
        userAccuracy.radiusM
      );
      ring.push([lng, lat]);
    }
    return {
      type: "FeatureCollection",
      features: [{ type: "Feature", properties: {}, geometry: { type: "Polygon", coordinates: [ring] } }],
    };
  };

  const refreshAccuracyCircle = () => {
    const source = ctx.getMap()?.getSource(ACCURACY_SOURCE) as maplibregl.GeoJSONSource | undefined;
    source?.setData(accuracyCircleFeature());
  };

  const updateUserPosition = (
    lat: number,
    lng: number,
    accuracyMeters: number | undefined,
    source: "gps" | "ip"
  ) => {
    const map = ctx.getMap();
    if (!map) return;
    if (!userMarker) {
      // Дефолтное выравнивание оставлено намеренно: конус направления
      // позиционируется своим transform (refreshUserHeadingVisual), поворот
      // элемента самим MapLibre применил бы bearing дважды.
      // setLngLat перед addTo: у Marker без позиции падает собственный
      // _update при первом же движении карты.
      userMarker = new maplibregl.Marker({ element: createUserLocationElement() })
        .setLngLat([lng, lat])
        .addTo(map);
    }
    const marker = userMarker;
    marker.setLngLat([lng, lat]);
    marker.getElement().classList.toggle("user-location-marker-icon--approximate", source === "ip");
    if (source === "ip") {
      setUserHeading(null);
    }

    userAccuracy =
      source === "gps" && typeof accuracyMeters === "number" && Number.isFinite(accuracyMeters)
        ? { lng, lat, radiusM: accuracyMeters }
        : null;
    refreshAccuracyCircle();

    locating.value = false;
    hasLocation.value = true;
    locationSource.value = source;
    // Только настоящий GPS: IP-фолбэк слишком грубый для «расстояния до вас».
    if (source === "gps") options.onGpsPosition?.(lat, lng);
    options.maybeCenterInitially(lng, lat);
  };

  // IP-геолокация точна максимум до города, зато работает без запроса
  // разрешения - фолбэк на случай отказа/недоступности GPS. geojs.io:
  // бесплатно, HTTPS, без API-ключа.
  const getIpLocationFallback = async (): Promise<{ lat: number; lng: number } | null> => {
    try {
      const res = await fetch("https://get.geojs.io/v1/ip/geo.json");
      if (!res.ok) return null;
      const data = await res.json();
      const lat = parseFloat(data?.latitude);
      const lng = parseFloat(data?.longitude);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
      return { lat, lng };
    } catch {
      return null;
    }
  };

  const fallbackToIpLocation = async () => {
    if (ipFallbackAttempted) {
      locating.value = false;
      return;
    }
    ipFallbackAttempted = true;
    const ipLocation = await getIpLocationFallback();
    if (ipLocation) {
      updateUserPosition(ipLocation.lat, ipLocation.lng, undefined, "ip");
    } else {
      locating.value = false;
    }
  };

  const handleGpsFix = (pos: GeolocationPosition) => {
    updateUserPosition(pos.coords.latitude, pos.coords.longitude, pos.coords.accuracy, "gps");
    if (typeof pos.coords.heading === "number" && !Number.isNaN(pos.coords.heading)) {
      setUserHeading(pos.coords.heading);
    }
  };

  const startWatchingPosition = () => {
    if (!navigator.geolocation) {
      void fallbackToIpLocation();
      return;
    }
    const options2: PositionOptions = { enableHighAccuracy: true, maximumAge: 10000, timeout: 10000 };
    // Сначала getCurrentPosition для быстрого первого фикса (у watchPosition
    // первый колбэк иногда медленный), затем watchPosition для живого
    // обновления маркера при движении.
    navigator.geolocation.getCurrentPosition(handleGpsFix, () => void fallbackToIpLocation(), options2);
    if (geoWatchId != null) return;
    geoWatchId = navigator.geolocation.watchPosition(
      handleGpsFix,
      () => {
        // Ошибка watch уже после первого фикса не страшна - на IP переходим
        // только если позиции не было вообще ни разу.
        if (!hasLocation.value) void fallbackToIpLocation();
      },
      options2
    );
  };

  const supportsOrientationPermission = () =>
    typeof (window as any).DeviceOrientationEvent?.requestPermission === "function";

  const handleOrientation = (event: DeviceOrientationEvent) => {
    const webkitHeading = (event as any).webkitCompassHeading;
    if (typeof webkitHeading === "number") {
      // iOS Safari: уже по часовой от истинного севера, конвертация не нужна.
      setUserHeading(webkitHeading);
      return;
    }
    if ((event as any).absolute && typeof event.alpha === "number") {
      setUserHeading(360 - event.alpha);
    }
  };

  // iOS 13+ требует DeviceOrientationEvent.requestPermission(), которое
  // разрешается положительно только при синхронном вызове из пользовательского
  // жеста - поэтому запрашивается из обработчика клика, а не при загрузке.
  // У Android/десктопа такого ограничения нет.
  const attachHeadingListenerAsync = async () => {
    if (orientationAttached) return;
    if (supportsOrientationPermission()) {
      try {
        const result = await (window as any).DeviceOrientationEvent.requestPermission();
        if (result !== "granted") return;
      } catch {
        return;
      }
    }
    orientationAttached = true;
    const eventName = "ondeviceorientationabsolute" in window ? "deviceorientationabsolute" : "deviceorientation";
    window.addEventListener(eventName, handleOrientation);
  };

  const detachHeadingListener = () => {
    if (!orientationAttached) return;
    const eventName = "ondeviceorientationabsolute" in window ? "deviceorientationabsolute" : "deviceorientation";
    window.removeEventListener(eventName, handleOrientation);
    orientationAttached = false;
  };

  // Выключение «показывать меня»: кнопка локации - тумблер, а не просто
  // «центрировать». Гасит слежение, маркер, круг точности и датчик
  // ориентации (батарея); следующее нажатие начнёт всё заново.
  const stopTracking = () => {
    if (geoWatchId != null) {
      navigator.geolocation?.clearWatch(geoWatchId);
      geoWatchId = null;
    }
    detachHeadingListener();
    userMarker?.remove();
    userMarker = null;
    userAccuracy = null;
    refreshAccuracyCircle();
    lastHeadingDeg = null;
    ipFallbackAttempted = false;
    locating.value = false;
    hasLocation.value = false;
    locationSource.value = null;
  };

  const handleLocateClick = () => {
    if (userMarker) {
      stopTracking();
      return;
    }
    void attachHeadingListenerAsync();
    locating.value = true;
    startWatchingPosition();
  };

  const getLastHeadingDeg = () => lastHeadingDeg;

  // Используется только при полном размонтировании MapView (см. onBeforeUnmount).
  const destroy = () => {
    if (geoWatchId != null) {
      navigator.geolocation?.clearWatch(geoWatchId);
      geoWatchId = null;
    }
    detachHeadingListener();
    userMarker?.remove();
    userMarker = null;
    userAccuracy = null;
  };

  return {
    locating,
    hasLocation,
    locationSource,
    refreshUserHeadingVisual,
    setUserHeading,
    startWatchingPosition,
    attachHeadingListener: () => void attachHeadingListenerAsync(),
    handleLocateClick,
    stopTracking,
    getLastHeadingDeg,
    destroy,
  };
}
